import { datadogRum } from "@datadog/browser-rum";
import request from "superagent";

import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
import { getAuthHeader } from "./superagent";

/**
 * Detects a superagent request that ended without ever producing an HTTP
 * response. superagent collapses several distinct browser conditions into one
 * "Request has been terminated" error (`crossDomain === true`): the network is
 * offline, the page is unloading mid-request, or the origin was blocked by
 * CORS. Client-side timeouts (`timeout === true`) and status 0 responses are
 * the same class of non-actionable transient failure.
 *
 * On `/agent/dashboard` these are overwhelmingly benign navigation/offline
 * terminations the user never notices, so routing them through `logError`
 * (`datadogRum.addError`) floods RUM Error Tracking (issue
 * 32ce4cd4-d162-11ee-8816-da7ad0900002, STAFF-1346). Error Tracking groups by
 * the raw error fingerprint and ignores metadata discriminators, so the only
 * way to keep it clean is to not emit an error for the expected case.
 *
 * A genuine API failure from the FSS service (4xx/5xx) always carries a numeric
 * `status`, so it is never treated as a termination and keeps its actionable
 * `logError` context.
 */
function isExpectedRequestTermination(error: unknown): boolean {
  if (typeof error !== "object" || error === null) {
    return false;
  }

  const { crossDomain, timeout, status } = error as {
    crossDomain?: boolean;
    timeout?: boolean;
    status?: number;
  };

  return crossDomain === true || timeout === true || status === 0;
}

export const getFSSProfilePicDistributionURL = async (
  workerId: string
  // @ts-expect-error TODO(typecheck-unify): TS7030 Not all code paths return a value.
): Promise<string | undefined> => {
  if (!workerId) {
    return;
  }

  try {
    const distributionURL = await request
      // @ts-expect-error TODO(typecheck-unify): TS7017 Element implicitly has an 'any' type because type 'typeof globalThis' has no index signature.
      .get(`${global.api}/agentProfile/${workerId}/profile-image-distribution-url`)
      .set(await getAuthHeader());
    return distributionURL.body?.distribution_url;
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    // Expected: the worker simply has no profile image. Not a failure.
    if (error?.status === 404) {
      return;
    }

    // Expected: the request was terminated before any HTTP response (offline,
    // page unload, CORS denial, or timeout). Demote to a non-error RUM action
    // so the rate stays observable without polluting Error Tracking.
    if (isExpectedRequestTermination(error)) {
      datadogRum.addAction(APP_V2_APP_EVENTS.GET_AGENT_PROFILE_PICTURE_URL_TERMINATED, {
        workerId,
      });
      return;
    }

    // Unexpected: a real HTTP failure (4xx/5xx) from the FSS service. Keep the
    // actionable error with full context.
    logError("Error fetching profile pic url", {
      error,
      metadata: {
        workerId,
      },
    });
  }
};
